import React from 'react';
import { FormControlLabel, Checkbox } from '@mui/material';
import type { FormControlLabelProps } from '@mui/material';

export interface CheckboxComponentProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'default';
  size?: 'small' | 'medium';
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
  required?: boolean;
  indeterminate?: boolean;
  sx?: FormControlLabelProps['sx'];
  labelSx?: React.CSSProperties;
}

/**
 * CheckboxComponent - Reusable Checkbox Input Component
 * 
 * A standardized checkbox component that provides:
 * - Consistent styling and behavior across the application
 * - Flexible label positioning and styling
 * - Support for different sizes and colors
 * - Disabled and indeterminate states
 * - Custom styling options
 */
export const CheckboxComponent: React.FC<CheckboxComponentProps> = ({
  label,
  checked,
  onChange,
  disabled = false,
  color = 'primary',
  size = 'medium',
  labelPlacement = 'end',
  required = false,
  indeterminate = false,
  sx,
  labelSx,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked);
  };

  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          color={color}
          size={size}
          required={required}
          indeterminate={indeterminate}
        />
      }
      label={label}
      labelPlacement={labelPlacement}
      sx={{
        alignSelf: 'flex-start',
        '& .MuiFormControlLabel-label': {
          fontSize: size === 'small' ? '0.75rem' : '0.875rem',
          color: disabled ? 'text.disabled' : 'text.secondary',
          ...labelSx,
        },
        ...sx,
      }}
    />
  );
};

export default CheckboxComponent;